// src/services/connectionMatchingService.ts
// Service voor het berekenen van match scores en gedeelde connecties voor "Open voor netwerk" lijst

import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import type { UserProfile } from '../types';

export interface ConnectionMatchData {
  sharedConnections: number;
  sharedInterests: string[];
  sharedExpertise: string[];
  sameIndustry: boolean;
  industry?: string;
  matchScore: number;
  matchReasons: string[];
  complementaryGoals: boolean;
  workScopeMatch: boolean;
}

export interface UserWithMatchData {
  userId: string;
  userName: string;
  userEmail: string;
  userPhoto?: string;
  userCompany?: string;
  userRegion?: string;
  userRegions?: string[];
  userBio?: string;
  subscriptionTier: 'free' | 'premium' | 'enterprise';
  createdAt?: Date | string;
  expiresAt?: Date | string;
  isActive?: boolean;
  matchData: ConnectionMatchData;
}

class ConnectionMatchingService {
  /**
   * Bereken gedeelde connecties tussen twee gebruikers
   */
  async calculateSharedConnections(userId1: string, userId2: string): Promise<number> {
    try {
      // Haal alle connecties op van beide gebruikers
      const [connections1, connections2] = await Promise.all([
        this.getUserConnections(userId1),
        this.getUserConnections(userId2)
      ]);

      // Vind intersectie (gedeelde connecties)
      const shared = connections1.filter(conn => connections2.includes(conn));
      return shared.length;
    } catch (error) {
      console.error('Error calculating shared connections:', error);
      return 0;
    }
  }

  /**
   * Haal alle connection IDs op van een gebruiker
   */
  private async getUserConnections(userId: string): Promise<string[]> {
    try {
      const connections1 = query(
        collection(db, 'connections'),
        where('user1Id', '==', userId)
      );
      const connections2 = query(
        collection(db, 'connections'),
        where('user2Id', '==', userId)
      );

      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(connections1),
        getDocs(connections2)
      ]);

      const connections: string[] = [];
      snapshot1.forEach(doc => {
        const data = doc.data();
        if (data.user2Id && data.user2Id !== userId) {
          connections.push(data.user2Id);
        }
      });
      snapshot2.forEach(doc => {
        const data = doc.data();
        if (data.user1Id && data.user1Id !== userId) {
          connections.push(data.user1Id);
        }
      });

      return Array.from(new Set(connections)); // Remove duplicates
    } catch (error) {
      console.error('Error getting user connections:', error);
      return [];
    }
  }

  /**
   * Bereken match data tussen huidige gebruiker en een andere gebruiker
   */
  async calculateMatchData(
    currentUserId: string,
    currentUserProfile: UserProfile,
    otherUserId: string,
    otherUserProfile: any
  ): Promise<ConnectionMatchData> {
    const matchData: ConnectionMatchData = {
      sharedConnections: 0,
      sharedInterests: [],
      sharedExpertise: [],
      sameIndustry: false,
      complementaryGoals: false,
      workScopeMatch: false,
      matchScore: 0,
      matchReasons: []
    };

    try {
      // 1. Gedeelde connecties (hoogste prioriteit)
      matchData.sharedConnections = await this.calculateSharedConnections(currentUserId, otherUserId);
      if (matchData.sharedConnections > 0) {
        matchData.matchReasons.push(`${matchData.sharedConnections} gedeelde connectie${matchData.sharedConnections > 1 ? 's' : ''}`);
      }

      // 2. Gedeelde interesses
      const currentInterests = currentUserProfile.interests || [];
      const otherInterests = otherUserProfile?.interests || [];
      matchData.sharedInterests = this.findSharedItems(currentInterests, otherInterests);
      if (matchData.sharedInterests.length > 0) {
        matchData.matchReasons.push(`Gedeelde interesses: ${matchData.sharedInterests.slice(0, 2).join(', ')}`);
      }

      // 3. Gedeelde expertise
      const currentExpertise = currentUserProfile.expertise || [];
      const otherExpertise = otherUserProfile?.expertise || [];
      matchData.sharedExpertise = this.findSharedItems(currentExpertise, otherExpertise);
      if (matchData.sharedExpertise.length > 0) {
        matchData.matchReasons.push(`Gedeelde expertise: ${matchData.sharedExpertise.slice(0, 2).join(', ')}`);
      }

      // 4. Zelfde industrie
      const currentIndustry = currentUserProfile.industry;
      const otherIndustry = otherUserProfile?.industry;
      if (currentIndustry && otherIndustry) {
        // Check if industries match (can be string or array)
        const currentIndustries = Array.isArray(currentIndustry) ? currentIndustry : [currentIndustry];
        const otherIndustries = Array.isArray(otherIndustry) ? otherIndustry : [otherIndustry];
        const normalizedCurrent = currentIndustries.map(i => i.toLowerCase().trim());
        const normalizedOther = otherIndustries.map(i => i.toLowerCase().trim());
        
        matchData.sameIndustry = normalizedCurrent.some(i => normalizedOther.includes(i));
        if (matchData.sameIndustry) {
          matchData.industry = otherIndustries[0];
          matchData.matchReasons.push(`Zelfde industrie: ${matchData.industry}`);
        }
      }

      // 5. Complementaire goals
      const currentGoals = currentUserProfile.goals || {};
      const otherGoals = otherUserProfile?.goals || {};
      
      // Check for complementary goals (e.g., one wants clients, other wants partnerships)
      const currentWantsClients = currentGoals.clients === true;
      const currentWantsPartners = currentGoals.partnerships === true;
      const otherWantsClients = otherGoals.clients === true;
      const otherWantsPartners = otherGoals.partnerships === true;
      
      if ((currentWantsClients && otherWantsPartners) || (currentWantsPartners && otherWantsClients)) {
        matchData.complementaryGoals = true;
        matchData.matchReasons.push('Complementaire doelen');
      }

      // 6. Work scope match
      const currentWorkScope = currentUserProfile.workScope;
      const otherWorkScope = otherUserProfile?.workScope;
      if (currentWorkScope && otherWorkScope) {
        matchData.workScopeMatch = currentWorkScope === otherWorkScope;
        if (matchData.workScopeMatch) {
          matchData.matchReasons.push(`Zelfde werkgebied: ${currentWorkScope}`);
        }
      }

      // 7. Bereken totale match score (0-100)
      matchData.matchScore = this.calculateMatchScore(matchData);

    } catch (error) {
      console.error('Error calculating match data:', error);
    }

    return matchData;
  }

  /**
   * Vind gedeelde items tussen twee arrays
   */
  private findSharedItems(array1: string[], array2: string[]): string[] {
    if (!array1 || !array2 || array1.length === 0 || array2.length === 0) {
      return [];
    }

    const normalized1 = array1.map(item => item.toLowerCase().trim());
    const normalized2 = array2.map(item => item.toLowerCase().trim());
    
    return normalized1.filter(item => normalized2.includes(item));
  }

  /**
   * Bereken totale match score (0-100)
   */
  private calculateMatchScore(matchData: ConnectionMatchData): number {
    let score = 0;
    let maxScore = 0;

    // Gedeelde connecties (40 punten max)
    const sharedConnectionsScore = Math.min(matchData.sharedConnections * 10, 40);
    score += sharedConnectionsScore;
    maxScore += 40;

    // Gedeelde interesses (25 punten max)
    const sharedInterestsScore = Math.min(matchData.sharedInterests.length * 8, 25);
    score += sharedInterestsScore;
    maxScore += 25;

    // Gedeelde expertise (15 punten max)
    const sharedExpertiseScore = Math.min(matchData.sharedExpertise.length * 7, 15);
    score += sharedExpertiseScore;
    maxScore += 15;

    // Zelfde industrie (10 punten)
    if (matchData.sameIndustry) {
      score += 10;
    }
    maxScore += 10;

    // Complementaire goals (5 punten)
    if (matchData.complementaryGoals) {
      score += 5;
    }
    maxScore += 5;

    // Work scope match (5 punten)
    if (matchData.workScopeMatch) {
      score += 5;
    }
    maxScore += 5;

    // Normalize to 0-100
    return Math.round((score / maxScore) * 100);
  }

  /**
   * Voeg match data toe aan lijst van gebruikers
   */
  async enrichUsersWithMatchData(
    currentUserId: string,
    currentUserProfile: UserProfile,
    users: Array<{
      userId: string;
      userName: string;
      userEmail: string;
      userPhoto?: string;
      userCompany?: string;
      userRegion?: string;
      userRegions?: string[];
      userBio?: string;
      subscriptionTier: 'free' | 'premium' | 'enterprise';
    }>
  ): Promise<UserWithMatchData[]> {
    try {
      // Haal alle user profiles op in batch
      const profilePromises = users.map(user => 
        getDoc(doc(db, 'userProfiles', user.userId)).catch(() => null)
      );
      const profileDocs = await Promise.all(profilePromises);

      // Bereken match data voor elke gebruiker
      const enrichedUsers: UserWithMatchData[] = [];
      
      for (let i = 0; i < users.length; i++) {
        const user = users[i];
        const profileDoc = profileDocs[i];
        
        if (profileDoc?.exists()) {
          const otherUserProfile = profileDoc.data();
          const matchData = await this.calculateMatchData(
            currentUserId,
            currentUserProfile,
            user.userId,
            otherUserProfile
          );

          enrichedUsers.push({
            ...user,
            matchData
          });
        } else {
          // Geen profile gevonden, gebruik default match data
          enrichedUsers.push({
            ...user,
            matchData: {
              sharedConnections: 0,
              sharedInterests: [],
              sharedExpertise: [],
              sameIndustry: false,
              complementaryGoals: false,
              workScopeMatch: false,
              matchScore: 0,
              matchReasons: []
            }
          });
        }
      }

      return enrichedUsers;
    } catch (error) {
      console.error('Error enriching users with match data:', error);
      // Return users without match data on error
      return users.map(user => ({
        ...user,
        matchData: {
          sharedConnections: 0,
          sharedInterests: [],
          sharedExpertise: [],
          sameIndustry: false,
          complementaryGoals: false,
          workScopeMatch: false,
          matchScore: 0,
          matchReasons: []
        }
      }));
    }
  }
}

export const connectionMatchingService = new ConnectionMatchingService();
export default connectionMatchingService;

